import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { QrCode, ArrowLeft, Download, Copy, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const QRCodeGenerator = () => {
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('url');
  const [qrSize, setQrSize] = useState(256);
  const [qrColor, setQrColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  // Generate QR using a public API
  const generateQR = async () => {
    if (!content) {
      toast.error('Please enter content for the QR code');
      return;
    }

    setLoading(true);
    try {
      // Using QR Server API
      const encodedContent = encodeURIComponent(content);
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodedContent}&color=${qrColor.replace('#', '')}&bgcolor=${bgColor.replace('#', '')}`;
      
      setQrDataUrl(qrUrl);
      toast.success('QR Code generated!');
    } catch (error) {
      toast.error('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    if (!qrDataUrl) {
      toast.error('Generate a QR code first');
      return;
    }

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qrcode-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code downloaded!');
  };

  const copyQRLink = async () => {
    if (!qrDataUrl) {
      toast.error('Generate a QR code first');
      return;
    }

    try {
      await navigator.clipboard.writeText(qrDataUrl);
      toast.success('QR image URL copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const getPlaceholder = () => {
    switch (contentType) {
      case 'url': return 'https://example.com';
      case 'text': return 'Enter any text message';
      case 'phone': return '+91 9876543210';
      case 'email': return 'email@example.com';
      case 'wifi': return 'WIFI:T:WPA;S:NetworkName;P:Password;;';
      case 'vcard': return 'BEGIN:VCARD\nVERSION:3.0\nN:Doe;John\nTEL:+1234567890\nEND:VCARD';
      default: return 'Enter content';
    }
  };

  const formatContent = () => {
    switch (contentType) {
      case 'phone':
        return `tel:${content}`;
      case 'email':
        return `mailto:${content}`;
      default:
        return content;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="qr-code-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/20 via-background to-pink-500/10 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <QrCode className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">QR Code Generator</h1>
              <p className="text-muted-foreground">Create QR codes for any content</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Options */}
          <div className="glass-card p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Content Type</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'url', label: 'URL' },
                  { id: 'text', label: 'Text' },
                  { id: 'phone', label: 'Phone' },
                  { id: 'email', label: 'Email' },
                  { id: 'wifi', label: 'WiFi' },
                  { id: 'vcard', label: 'vCard' }
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setContentType(type.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      contentType === type.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content</label>
              {contentType === 'vcard' || contentType === 'wifi' ? (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={getPlaceholder()}
                  rows={5}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none font-mono text-sm"
                  data-testid="content-input"
                />
              ) : (
                <input
                  type={contentType === 'email' ? 'email' : contentType === 'url' ? 'url' : 'text'}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={getPlaceholder()}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  data-testid="content-input"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Size</label>
                <select
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  <option value={128}>Small (128px)</option>
                  <option value={256}>Medium (256px)</option>
                  <option value={512}>Large (512px)</option>
                  <option value={1024}>Extra Large (1024px)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">QR Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="w-14 h-12 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="flex-1 px-3 py-3 bg-white/5 border border-white/10 rounded-xl font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Background Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-14 h-12 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="flex-1 px-3 py-3 bg-white/5 border border-white/10 rounded-xl font-mono text-sm"
                />
              </div>
            </div>

            <button
              onClick={generateQR}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="generate-qr-btn"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <QrCode className="w-5 h-5" />
              )}
              Generate QR Code
            </button>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">Preview</h3>
            <div 
              className="aspect-square rounded-xl flex items-center justify-center"
              style={{ backgroundColor: bgColor }}
            >
              {qrDataUrl ? (
                <img 
                  src={qrDataUrl} 
                  alt="QR Code" 
                  className="max-w-full max-h-full"
                  data-testid="qr-image"
                />
              ) : (
                <div className="text-center p-8">
                  <QrCode className="w-24 h-24 mx-auto mb-4 text-gray-400" />
                  <p className="text-muted-foreground">
                    Enter content and click generate
                  </p>
                </div>
              )}
            </div>

            {qrDataUrl && (
              <div className="mt-4 flex gap-3">
                <button
                  onClick={downloadQR}
                  className="flex-1 py-3 bg-purple-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-purple-600 transition-colors"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={copyQRLink}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy URL
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 glass-card p-6">
          <h3 className="font-semibold mb-4">QR Code Content Types</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">URL</p>
              <p className="text-muted-foreground">Link to any website or page</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">Phone</p>
              <p className="text-muted-foreground">Opens dialer with number</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">WiFi</p>
              <p className="text-muted-foreground">Share WiFi credentials easily</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">Email</p>
              <p className="text-muted-foreground">Opens email client</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">vCard</p>
              <p className="text-muted-foreground">Share contact info</p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="font-medium text-primary mb-2">Text</p>
              <p className="text-muted-foreground">Any plain text message</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
